import React, { useState } from 'react';
import { useEntity } from '../contexts/EntityContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Plus, Edit2, Trash2, Building2, Settings2 } from 'lucide-react';

export function Settings() {
  const { entities, selectedEntity, createEntity, updateEntity, deleteEntity, setSelectedEntity } = useEntity();
  const { t, language, setLanguage } = useLanguage();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingEntity, setEditingEntity] = useState(null);

  const [formData, setFormData] = useState({
    name: '',
    entity_type: 'dutch_bv',
    country: 'NL',
    is_holding: false,
    parent_entity_id: '',
    address: '',
    tax_id: '',
    logo_url: '',
    bank_name: '',
    bank_account: '',
    bank_routing: '',
    bank_routing_wire: '',
    bank_swift: '',
    bank_swift_foreign: '',
    bank_address: '',
    // NL-specific
    rsin: '',
    btw_nummer: '',
    btw_id: '',
    loonheffingsnummer: '',
    kvk_date: '',
    // US-specific
    ubi_number: '',
    duns_number: '',
    state_registration_date: '',
  });

  const entityTypes = [
    { value: 'dutch_bv', label: language === 'nl' ? 'Nederlandse BV' : 'Dutch BV', country: 'NL' },
    { value: 'us_ccorp', label: 'US C-Corporation', country: 'US' }
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      ...formData,
      country: entityTypes.find(t => t.value === formData.entity_type)?.country || 'NL',
      parent_entity_id: (formData.parent_entity_id && formData.parent_entity_id !== 'none') ? formData.parent_entity_id : null,
      logo_url: formData.logo_url || null
    };

    try {
      let result;
      if (editingEntity) {
        result = await updateEntity(editingEntity.entity_id, payload);
      } else {
        result = await createEntity(payload);
        if (result && entities.length === 0) {
          setSelectedEntity(result);
        }
      }
      
      if (result) {
        resetForm();
        setDialogOpen(false);
      } else {
        // Show inline error if API returned null
        alert(language === 'nl' ? 'Opslaan mislukt. Controleer de gegevens.' : 'Save failed. Check the data.');
      }
    } catch (err) {
      console.error('Entity save error:', err);
      alert(language === 'nl' ? 'Er ging iets mis bij het opslaan.' : 'Something went wrong while saving.');
    }
  };

  const handleEdit = (entity) => {
    setEditingEntity(entity);
    setFormData({
      name: entity.name,
      entity_type: entity.entity_type,
      country: entity.country,
      is_holding: entity.is_holding,
      parent_entity_id: entity.parent_entity_id || '',
      address: entity.address || '',
      tax_id: entity.tax_id || '',
      logo_url: entity.logo_url || '',
      bank_name: entity.bank_name || '',
      bank_account: entity.bank_account || '',
      bank_routing: entity.bank_routing || '',
      bank_routing_wire: entity.bank_routing_wire || '',
      bank_swift: entity.bank_swift || '',
      bank_swift_foreign: entity.bank_swift_foreign || '',
      bank_address: entity.bank_address || '',
      rsin: entity.rsin || '',
      btw_nummer: entity.btw_nummer || '',
      btw_id: entity.btw_id || '',
      loonheffingsnummer: entity.loonheffingsnummer || '',
      kvk_date: entity.kvk_date || '',
      ubi_number: entity.ubi_number || '',
      duns_number: entity.duns_number || '',
      state_registration_date: entity.state_registration_date || '',
    });
    setDialogOpen(true);
  };

  const handleDelete = async (entityId) => {
    if (!window.confirm(language === 'nl' 
      ? 'Weet je zeker dat je deze entiteit wilt verwijderen? Dit verwijdert ook alle gerelateerde gegevens.'
      : 'Are you sure you want to delete this entity? This will also delete all related data.')) {
      return;
    }
    await deleteEntity(entityId);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      entity_type: 'dutch_bv',
      country: 'NL',
      is_holding: false,
      parent_entity_id: '',
      address: '',
      tax_id: '',
      logo_url: '',
      bank_name: '',
      bank_account: '',
      bank_routing: '',
      bank_routing_wire: '',
      bank_swift: '',
      bank_swift_foreign: '',
      bank_address: '',
      rsin: '',
      btw_nummer: '',
      btw_id: '',
      loonheffingsnummer: '',
      kvk_date: '',
      ubi_number: '',
      duns_number: '',
      state_registration_date: '',
    });
    setEditingEntity(null);
  };

  const getEntityBadge = (entity) => {
    if (entity.is_holding) return 'entity-badge entity-badge-holding';
    if (entity.country === 'NL') return 'entity-badge entity-badge-nl';
    return 'entity-badge entity-badge-us';
  };

  const getEntityLabel = (entity) => {
    if (entity.is_holding) return 'Holding';
    if (entity.country === 'NL') return 'BV';
    return 'C-Corp';
  };

  // Get potential parent entities (holdings)
  const holdingEntities = entities.filter(e => e.is_holding);

  return (
    <div className="animate-fade-in space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight" style={{ fontFamily: 'Manrope, sans-serif' }}>
          {t('nav.settings')}
        </h1>
        <p className="text-muted-foreground">
          {language === 'nl' ? 'Beheer je entiteiten en instellingen' : 'Manage your entities and settings'}
        </p>
      </div>

      {/* Language Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Settings2 className="w-5 h-5" />
            {language === 'nl' ? 'Taalinstellingen' : 'Language Settings'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium">
                {language === 'nl' ? 'Interface Taal' : 'Interface Language'}
              </p>
              <p className="text-sm text-muted-foreground">
                {language === 'nl' 
                  ? 'Kies de taal voor de Nederlandse entiteiten' 
                  : 'Choose language for Dutch entities'}
              </p>
            </div>
            <Select value={language} onValueChange={setLanguage}>
              <SelectTrigger className="w-40" data-testid="language-select">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nl">Nederlands</SelectItem>
                <SelectItem value="en">English</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Entities Management */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <Building2 className="w-5 h-5" />
            {language === 'nl' ? 'Entiteiten' : 'Entities'}
          </CardTitle>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button className="btn-brand" data-testid="add-entity-btn">
                <Plus className="w-4 h-4 mr-2" />
                {language === 'nl' ? 'Entiteit Toevoegen' : 'Add Entity'}
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle style={{ fontFamily: 'Manrope, sans-serif' }}>
                  {editingEntity
                    ? (language === 'nl' ? 'Entiteit Bewerken' : 'Edit Entity')
                    : (language === 'nl' ? 'Nieuwe Entiteit' : 'New Entity')}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* === GENERAL INFO === */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    {language === 'nl' ? 'Algemeen' : 'General'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label>{language === 'nl' ? 'Bedrijfsnaam' : 'Company Name'}</Label>
                      <Input
                        data-testid="entity-name"
                        value={formData.name}
                        onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                        placeholder={language === 'nl' ? 'Mijn Bedrijf B.V.' : 'My Company Inc.'}
                        required
                      />
                    </div>

                    <div>
                      <Label>{language === 'nl' ? 'Type Entiteit' : 'Entity Type'}</Label>
                      <Select
                        value={formData.entity_type}
                        onValueChange={(value) => setFormData({ 
                          ...formData, 
                          entity_type: value,
                          country: entityTypes.find(t => t.value === value)?.country || 'NL'
                        })}
                      >
                        <SelectTrigger data-testid="entity-type">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {entityTypes.map(type => (
                            <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div className="flex items-center gap-3 pt-5">
                      <Switch
                        data-testid="is-holding"
                        checked={formData.is_holding}
                        onCheckedChange={(checked) => setFormData({ ...formData, is_holding: checked })}
                      />
                      <div>
                        <Label className="cursor-pointer">{language === 'nl' ? 'Holding' : 'Holding Company'}</Label>
                      </div>
                    </div>

                    {!formData.is_holding && holdingEntities.length > 0 && (
                      <div className="md:col-span-2">
                        <Label>{language === 'nl' ? 'Moederbedrijf (optioneel)' : 'Parent Company (optional)'}</Label>
                        <Select
                          value={formData.parent_entity_id || 'none'}
                          onValueChange={(value) => setFormData({ ...formData, parent_entity_id: value === 'none' ? '' : value })}
                        >
                          <SelectTrigger data-testid="parent-entity">
                            <SelectValue placeholder={language === 'nl' ? 'Selecteer holding' : 'Select holding'} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">
                              {language === 'nl' ? 'Geen' : 'None'}
                            </SelectItem>
                            {holdingEntities.map(entity => (
                              <SelectItem key={entity.entity_id} value={entity.entity_id}>
                                {entity.name}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    )}

                    <div className="md:col-span-2">
                      <Label>{language === 'nl' ? 'Adres' : 'Address'}</Label>
                      <Textarea
                        data-testid="entity-address"
                        value={formData.address}
                        onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                        placeholder={language === 'nl' ? 'Straat 123\n1234 AB Amsterdam' : '123 Main St\nCity, State ZIP'}
                        rows={2}
                      />
                    </div>
                  </div>
                </div>

                {/* === REGISTRATION / TAX IDs === */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    {language === 'nl' ? 'Registratie & Fiscaal' : 'Registration & Tax'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formData.country === 'NL' ? (
                      <>
                        <div>
                          <Label>KVK Nummer</Label>
                          <Input
                            data-testid="entity-tax-id"
                            value={formData.tax_id}
                            onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                            placeholder="12345678"
                          />
                        </div>
                        <div>
                          <Label>{language === 'nl' ? 'KVK Registratiedatum' : 'KVK Registration Date'}</Label>
                          <Input
                            data-testid="entity-kvk-date"
                            value={formData.kvk_date}
                            onChange={(e) => setFormData({ ...formData, kvk_date: e.target.value })}
                            placeholder="DD-MM-JJJJ"
                          />
                        </div>
                        <div>
                          <Label>RSIN / Fiscaal Nummer</Label>
                          <Input
                            data-testid="entity-rsin"
                            value={formData.rsin}
                            onChange={(e) => setFormData({ ...formData, rsin: e.target.value })}
                            placeholder="123456789"
                          />
                        </div>
                        <div>
                          <Label>BTW Nummer</Label>
                          <Input
                            data-testid="entity-btw-nummer"
                            value={formData.btw_nummer}
                            onChange={(e) => setFormData({ ...formData, btw_nummer: e.target.value })}
                            placeholder="NL123456789B01"
                          />
                        </div>
                        <div>
                          <Label>BTW Identificatienummer</Label>
                          <Input
                            data-testid="entity-btw-id"
                            value={formData.btw_id}
                            onChange={(e) => setFormData({ ...formData, btw_id: e.target.value })}
                            placeholder="NL123456789B01"
                          />
                        </div>
                        <div>
                          <Label>Loonheffingsnummer</Label>
                          <Input
                            data-testid="entity-loonheffing"
                            value={formData.loonheffingsnummer}
                            onChange={(e) => setFormData({ ...formData, loonheffingsnummer: e.target.value })}
                            placeholder="123456789L01"
                          />
                        </div>
                      </>
                    ) : (
                      <>
                        <div>
                          <Label>EIN (Employer Identification Number)</Label>
                          <Input
                            data-testid="entity-tax-id"
                            value={formData.tax_id}
                            onChange={(e) => setFormData({ ...formData, tax_id: e.target.value })}
                            placeholder="XX-XXXXXXX"
                          />
                        </div>
                        <div>
                          <Label>UBI Number (WA State)</Label>
                          <Input
                            data-testid="entity-ubi"
                            value={formData.ubi_number}
                            onChange={(e) => setFormData({ ...formData, ubi_number: e.target.value })}
                            placeholder="XXX-XXX-XXX"
                          />
                        </div>
                        <div>
                          <Label>D-U-N-S Number</Label>
                          <Input
                            data-testid="entity-duns"
                            value={formData.duns_number}
                            onChange={(e) => setFormData({ ...formData, duns_number: e.target.value })}
                            placeholder="XX-XXX-XXXX"
                          />
                        </div>
                        <div>
                          <Label>State Registration Date</Label>
                          <Input
                            data-testid="entity-state-reg-date"
                            value={formData.state_registration_date}
                            onChange={(e) => setFormData({ ...formData, state_registration_date: e.target.value })}
                            placeholder="MM/DD/YYYY"
                          />
                        </div>
                      </>
                    )}
                  </div>
                </div>

                {/* === BANK DETAILS === */}
                <div>
                  <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider mb-3">
                    {language === 'nl' ? 'Bankgegevens' : 'Bank Details'}
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label>{language === 'nl' ? 'Bank' : 'Bank Name'}</Label>
                      <Input
                        data-testid="entity-bank"
                        value={formData.bank_name}
                        onChange={(e) => setFormData({ ...formData, bank_name: e.target.value })}
                        placeholder={formData.country === 'NL' ? 'ABN AMRO' : 'Bank of America'}
                      />
                    </div>

                    <div>
                      <Label>{formData.country === 'NL' ? 'IBAN' : 'Account Number'}</Label>
                      <Input
                        data-testid="entity-account"
                        value={formData.bank_account}
                        onChange={(e) => setFormData({ ...formData, bank_account: e.target.value })}
                        placeholder={formData.country === 'NL' ? 'NL00 ABNA 0000 0000 00' : 'XXXX-XXXX-XXXX'}
                      />
                    </div>

                    <div>
                      <Label>{formData.country === 'NL' ? 'BIC Code' : 'SWIFT Code'}</Label>
                      <Input
                        data-testid="entity-swift"
                        value={formData.bank_swift}
                        onChange={(e) => setFormData({ ...formData, bank_swift: e.target.value })}
                        placeholder={formData.country === 'NL' ? 'ABNANL2A' : 'BOFAUS3N'}
                      />
                    </div>

                    {formData.country === 'US' && (
                      <>
                        <div>
                          <Label>Routing Number (Paper & Electronic)</Label>
                          <Input
                            data-testid="entity-routing"
                            value={formData.bank_routing}
                            onChange={(e) => setFormData({ ...formData, bank_routing: e.target.value })}
                            placeholder="XXXXXXXXX"
                          />
                        </div>
                        <div>
                          <Label>Wire Routing Number</Label>
                          <Input
                            data-testid="entity-routing-wire"
                            value={formData.bank_routing_wire}
                            onChange={(e) => setFormData({ ...formData, bank_routing_wire: e.target.value })}
                            placeholder="XXXXXXXXX"
                          />
                        </div>
                        <div>
                          <Label>SWIFT (Foreign Currency)</Label>
                          <Input
                            data-testid="entity-swift-foreign"
                            value={formData.bank_swift_foreign}
                            onChange={(e) => setFormData({ ...formData, bank_swift_foreign: e.target.value })}
                            placeholder="BOFAUS3N"
                          />
                        </div>
                      </>
                    )}

                    <div className={formData.country === 'US' ? 'md:col-span-2' : ''}>
                      <Label>{language === 'nl' ? 'Bankadres' : 'Bank Address'}</Label>
                      <Input
                        data-testid="entity-bank-address"
                        value={formData.bank_address}
                        onChange={(e) => setFormData({ ...formData, bank_address: e.target.value })}
                        placeholder={language === 'nl' ? 'Bankadres voor overboekingen' : 'Bank address for wire transfers'}
                      />
                    </div>
                  </div>
                </div>

                {/* === LOGO === */}
                <div>
                  <Label>{language === 'nl' ? 'Logo URL (optioneel)' : 'Logo URL (optional)'}</Label>
                  <Input
                    data-testid="entity-logo"
                    value={formData.logo_url}
                    onChange={(e) => setFormData({ ...formData, logo_url: e.target.value })}
                    placeholder="https://..."
                  />
                </div>

                <div className="flex gap-3 pt-4 sticky bottom-0 bg-background pb-2">
                  <Button type="button" variant="outline" className="flex-1" onClick={() => { setDialogOpen(false); resetForm(); }}>
                    {t('common.cancel')}
                  </Button>
                  <Button type="submit" className="flex-1 btn-brand" data-testid="save-entity-btn">
                    {t('common.save')}
                  </Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </CardHeader>
        <CardContent>
          {entities.length > 0 ? (
            <div className="space-y-3">
              {entities.map(entity => {
                const parentEntity = entity.parent_entity_id 
                  ? entities.find(e => e.entity_id === entity.parent_entity_id)
                  : null;
                
                return (
                  <div 
                    key={entity.entity_id}
                    className={`flex items-center justify-between p-4 border rounded-lg ${
                      selectedEntity?.entity_id === entity.entity_id ? 'border-[hsl(var(--brand))] bg-[hsl(var(--brand))]/5' : 'border-border'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      {entity.logo_url ? (
                        <img src={entity.logo_url} alt={entity.name} className="w-10 h-10 rounded object-contain" />
                      ) : (
                        <div className="w-10 h-10 bg-muted rounded flex items-center justify-center">
                          <Building2 className="w-5 h-5 text-muted-foreground" />
                        </div>
                      )}
                      <div>
                        <div className="flex items-center gap-2">
                          <p className="font-medium">{entity.name}</p>
                          <span className={getEntityBadge(entity)}>
                            {getEntityLabel(entity)}
                          </span>
                        </div>
                        <p className="text-sm text-muted-foreground">
                          {entity.tax_id && `${entity.country === 'NL' ? 'KVK' : 'EIN'}: ${entity.tax_id}`}
                          {entity.country === 'NL' && entity.btw_nummer && (
                            <span className="ml-2">| BTW: {entity.btw_nummer}</span>
                          )}
                          {entity.country === 'US' && entity.ubi_number && (
                            <span className="ml-2">| UBI: {entity.ubi_number}</span>
                          )}
                          {parentEntity && (
                            <span className="ml-2">
                              → {parentEntity.name}
                            </span>
                          )}
                        </p>
                      </div>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        variant="ghost" 
                        size="sm"
                        onClick={() => setSelectedEntity(entity)}
                        data-testid={`select-entity-${entity.entity_id}`}
                      >
                        {language === 'nl' ? 'Selecteer' : 'Select'}
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleEdit(entity)}>
                        <Edit2 className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => handleDelete(entity.entity_id)}>
                        <Trash2 className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="py-12 text-center">
              <Building2 className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <p className="text-muted-foreground mb-4">
                {language === 'nl' 
                  ? 'Nog geen entiteiten. Voeg je eerste bedrijf toe!'
                  : 'No entities yet. Add your first company!'}
              </p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Info Card */}
      <Card className="border-dashed">
        <CardContent className="py-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-muted rounded-lg flex items-center justify-center">
              <Building2 className="w-6 h-6 text-muted-foreground" />
            </div>
            <div>
              <h3 className="font-semibold mb-1" style={{ fontFamily: 'Manrope, sans-serif' }}>
                {language === 'nl' ? 'Bedrijfsstructuur' : 'Company Structure'}
              </h3>
              <p className="text-sm text-muted-foreground">
                {language === 'nl' 
                  ? 'Maak eerst een holding entiteit aan, dan kun je de andere entiteiten eronder hangen. Dit helpt bij het genereren van geconsolideerde rapporten.'
                  : 'Create a holding entity first, then you can add subsidiaries under it. This helps with generating consolidated reports.'}
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
